import React, { useEffect, useRef, useMemo } from 'react';
import { TextField } from 'new/elements/TextField';
import styles from './SelectedDatabaseInputs.module.scss';
import { ChangeEventHandler, FormEventHandler } from 'react';
import { ReactComponent as CancelIcon } from 'new/components/icons/fi-sr-cross-circle.svg';
import { Typography } from '@mui/material';
import { ReactComponent as TableIcon } from 'new/components/icons/fi-rr-table.svg';
import { ReactComponent as DatabaseIcon } from 'new/components/icons/fi-rr-database.svg';
import { MacroEditor, MacroEditorConfigProvider } from 'new/elements/MacroEditor';
import {
  extractDatasetFromApiServerFlow,
  extractMacrosFromApiServerFlow,
} from 'js/lib/MacroReplacementUtils';
import { trim, isEmpty } from 'lodash';

interface SelectedDatabaseInputsProps {
  selectDatabase: (value: string) => void;
  selectTable: (value: string) => void;
  selectedDatabaseId: string;
  selectedTableId: string;
  databasePlaceholder: string;
  tablePlaceholder: string;
  rightAction?: React.ReactNode;
  isApiServerFlow?: boolean;
  parentDataSet?: any;
  parentDataSetSamples?: any;
}

/**
 * Helper function to get macro suggestions from parent dataset
 * Returns an array of macro suggestions for use in the MacroEditor
 */
const getMacroSuggestions = (parentDataSet: any, parentDataSetSamples: any) => {
  if (
    !parentDataSet ||
    !parentDataSetSamples ||
    isEmpty(parentDataSet) ||
    isEmpty(parentDataSetSamples)
  ) {
    return [];
  }

  try {
    const parentDataSetMacros = extractMacrosFromApiServerFlow(parentDataSet, parentDataSetSamples);
    return !isEmpty(parentDataSetMacros) ? parentDataSetMacros : [];
  } catch (error) {
    return [];
  }
};

const SelectedDatabaseInputs = ({
  databasePlaceholder = 'Select a database below or type the name here',
  tablePlaceholder = 'Select a table below or type the name here',
  selectDatabase,
  selectTable,
  selectedDatabaseId,
  selectedTableId,
  rightAction,
  isApiServerFlow,
  parentDataSet,
  parentDataSetSamples,
}: SelectedDatabaseInputsProps) => {
  const refDb = useRef<HTMLInputElement>();
  const refTable = useRef<HTMLInputElement>();

  // Memoize macro suggestions to avoid recalculating on every render
  const macroSuggestions = useMemo(() => {
    const suggestions = getMacroSuggestions(parentDataSet, parentDataSetSamples);
    return suggestions;
  }, [parentDataSet, parentDataSetSamples]);

  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> &
    FormEventHandler<HTMLLabelElement> = (event) => {
    const { value, name } = event.target;

    if (!value) {
      return handleClearClick(name);
    }

    if (name === 'databaseName') {
      selectDatabase(value);
    } else if (name === 'tableName') {
      selectTable(`${selectedDatabaseId}/${value}`);
    }

    return;
  };

  const handleClearClick = (fieldName: string) => {
    if (fieldName === 'databaseName') {
      selectDatabase('');
    } else if (fieldName === 'tableName') {
      selectTable('');
    }
  };

  const dataBaseNameValue = selectedDatabaseId || '';

  const tableNameValue = selectedTableId?.split('/')[1] || '';

  // Only update ref values when NOT using MacroEditor (i.e., not in API server flow)
  useEffect(() => {
    if (!isApiServerFlow && refDb.current) {
      refDb.current.value = dataBaseNameValue;
    }
  }, [dataBaseNameValue, isApiServerFlow]);

  useEffect(() => {
    if (!isApiServerFlow && refTable.current) {
      refTable.current.value = tableNameValue;
    }
  }, [tableNameValue, isApiServerFlow]);

  const getTextField = (
    ref: React.RefObject<HTMLInputElement>,
    inputProps: any,
    placeholder: string,
    name: string,
    defaultValue: string,
  ) => {
    if (isApiServerFlow) {
      return (
        <MacroEditorConfigProvider
          parentDatasetSamples={extractDatasetFromApiServerFlow(
            parentDataSet || {},
            parentDataSetSamples || {},
          )}
        >
          <MacroEditor
            rows={1}
            value={defaultValue || ''}
            sx={{
              color: 'var(--color-greyscale-800)',
              fontSize: '0.875rem',
              lineHeight: '1.5rem',
            }}
            onChange={(newValue) => {
              const trimmedValue = trim(newValue);
              if (name === 'databaseName') {
                // Every parent's selectDatabase toggles the database off when handed the
                // current id (list-click semantics), so a trimmed-back keystroke must not
                // re-send it.
                if (trimmedValue !== dataBaseNameValue) selectDatabase(trimmedValue);
              } else if (name === 'tableName') {
                const tableId = trimmedValue ? `${selectedDatabaseId}/${trimmedValue}` : '';
                selectTable(tableId);
              }
            }}
            suggestions={macroSuggestions}
            placeholder={placeholder}
          />
        </MacroEditorConfigProvider>
      );
    }
    return (
      <TextField
        inputRef={ref}
        inputProps={inputProps}
        InputProps={{
          style: {
            color: 'var(--color-greyscale-800)',
            fontSize: '0.875rem',
            lineHeight: '1.5rem',
          },
          endAdornment: (
            <CancelIcon
              style={{
                color: 'var(--color-greyscale-200)',
                cursor: 'pointer',
                visibility: defaultValue ? 'visible' : 'hidden',
              }}
              onClick={() => handleClearClick(name)}
            />
          ),
        }}
        placeholder={placeholder}
        name={name}
        onChange={handleChange}
        defaultValue={defaultValue || ''}
        className={styles.input}
      />
    );
  };

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <div className={styles.typographyWrapper}>
          <Typography variant="body3" className={styles.typography}>
            Database
          </Typography>
          <DatabaseIcon className={styles.typographyIcon} />
        </div>
        {getTextField(
          refDb,
          { 'data-testid': 'database-input' },
          databasePlaceholder,
          'databaseName',
          dataBaseNameValue,
        )}
      </div>
      <div className={styles.inputWrapper}>
        <div className={styles.typographyWrapper}>
          <Typography variant="body3" className={styles.typography}>
            Table
          </Typography>
          <TableIcon className={styles.typographyIcon} />
        </div>
        {getTextField(
          refTable,
          { 'data-testid': 'table-input' },
          tablePlaceholder,
          'tableName',
          tableNameValue,
        )}
      </div>
      <div className={styles.rightAction}>{rightAction}</div>
    </div>
  );
};

export default SelectedDatabaseInputs;
